// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
import nds from '@nerdwallet/base-styles/classes';
import { Button, Type, Text, Accent, Box } from '@nerdwallet/currency';
import Image from '../image/image';
import getTextColor from '~/client/lib/text-color-config';

import {
  titleInput,
  stringInputSpec,
  boolCheckboxSpec,
} from '../../lib/editing-configs';
import parseMdLinks from '../../lib/parse-md-links';

import './icon-header.less';

const IconHeader = ({
  iconEyebrow,
  iconImage,
  iconImageAlt,
  title,
  linkButton,
  linkButtonText,
  linkButtonUrl,
  linkUrl,
  fontChronicle,
}) => {
  const { textColor } = getTextColor({ textColor: 'neutral-darker' });
  const colorStyle = { color: textColor };
  return (
    <div>
      <Box
        className={classNames([
          'icon-eyebrow',
          nds('margin-bottom-3'),
          nds('margin-horizontal-auto'),
        ])}
      >
        <Accent size="small" style={colorStyle}>
          {iconEyebrow}
        </Accent>
      </Box>
      <div
        className={classNames([
          'icon-header',
          nds('display-flex'),
          nds('flex-direction-row'),
          nds('align-items-center'),
          nds('padding-bottom-3'),
          nds('margin-top-0'),
          nds('margin-horizontal-auto'),
          nds('border-bottom-1'),
          nds('border-neutral-lighter'),
          nds('margin-bottom-2'),
          nds('margin-bottom-4--wide'),
        ])}
      >
        <div
          className={classNames(['icon-header__icon', nds('margin-right-2')])}
        >
          <Image src={iconImage} alt={iconImageAlt} />
        </div>
        <div
          className={classNames([
            'icon-header__content',
            nds('text-align-left'),
            nds('flex-1'),
          ])}
        >
          <Box
            className={classNames('icon-header__title', {
              chronicle: fontChronicle,
            })}
          >
            <Text size="large" bold>
              {title}
            </Text>
          </Box>
        </div>
        {linkButton ? (
          <Button
            href={linkButtonUrl}
            primary
            className={classNames(['icon-header__link'])}
          >
            {linkButtonText}
          </Button>
        ) : (
          <div
            className={classNames([
              'icon-header__link',
              nds('display-flex'),
              nds('margin-top-2'),
              nds('margin-left-auto--wide'),
            ])}
          >
            <Type
              className={classNames([
                'icon-header__linkUrl',
                nds('text-align-right'),
              ])}
            >
              {parseMdLinks(linkUrl)}
            </Type>
          </div>
        )}
      </div>
    </div>
  );
};

IconHeader.propTypes = {
  iconEyebrow: PropTypes.string,
  linkButton: PropTypes.bool,
  linkButtonText: PropTypes.string,
  linkButtonUrl: PropTypes.string,
  iconImage: PropTypes.string,
  iconImageAlt: PropTypes.string,
  title: PropTypes.string,
  linkUrl: PropTypes.string,
  fontChronicle: PropTypes.bool,
};

IconHeader.defaultProps = {
  iconEyebrow: '',
  iconImage: '',
  iconImageAlt: '',
  title: 'Title goes here',
  linkButton: false,
  linkUrl: '',
  fontChronicle: false,
};

IconHeader.getEditingConfig = (componentDocument) => {
  const optionalSelect =
    get(componentDocument, 'props.linkButton', '') === true
      ? [
          stringInputSpec('linkButtonText', 'Button Text'),
          stringInputSpec('linkButtonUrl', 'Button Link URL'),
        ]
      : [
          stringInputSpec('linkUrl', 'Details Text + URL', {
            help: `Want a hyperlink? Write it like this: [Hi!](www.thing.com).
                   NW links should be relative, e.g., [Home](/home)`,
          }),
        ];

  return [
    stringInputSpec('iconEyebrow', 'Icon Eyebrow Text'),
    stringInputSpec('iconImage', 'Icon Image URL'),
    stringInputSpec('iconImageAlt', 'Icon Image Alt Text'),
    titleInput,
    boolCheckboxSpec('linkButton', 'Make Details Text a Button?'),
    ...optionalSelect,
    boolCheckboxSpec('fontChronicle', 'Title in Chronicle?'),
  ];
};

IconHeader.displayName = 'IconHeader';

export default IconHeader;
